import { FC, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { X, CheckCircle2 } from 'lucide-react';
import { PageCard, SectionLabel, TOKENS } from '../../../components/design-system';
import { activateStock, listProjectsForCompany, ActivationBoxPayload } from '../api';
import { ManufacturerStock } from '../types';

interface Props {
  stock: ManufacturerStock;
  companies: Array<{ id: number; name: string }>;
  defaultCompanyId: number | null;
  onClose: () => void;
}

// All boxes activate as type=1 (online / real-time) per user spec.
// Sensitivity and redundancy fields are deliberately out — admin can
// tune them later via the normal box edit flow if/when needed. Keeps
// the activation modal a quick gate (just empresa + projeto + nome +
// endereço + senha) instead of a 20-field form.
const ALWAYS_ONLINE = 1;

export const ActivateStockModal: FC<Props> = ({ stock, companies, defaultCompanyId, onClose }) => {
  const qc = useQueryClient();

  const [companyId, setCompanyId] = useState<string>(
    String(stock.intended_company_id ?? defaultCompanyId ?? ''),
  );
  const [projectId, setProjectId] = useState<string>('');
  const [name, setName] = useState<string>(`CMB ${stock.cmb_number}`);
  const [address, setAddress] = useState<string>('');
  const [defaultPassword, setDefaultPassword] = useState<string>('');

  const numericCompanyId = Number(companyId);

  // Project picker is scoped to the chosen company. Switching company
  // resets the project — the prior one likely belongs to a different
  // company and the API would reject it.
  const projectsQuery = useQuery(
    ['projects-for-company', numericCompanyId],
    () => listProjectsForCompany(numericCompanyId),
    { enabled: numericCompanyId > 0, staleTime: 30_000 },
  );
  useEffect(() => { setProjectId(''); }, [companyId]);

  const activate = useMutation(
    () => {
      const box: ActivationBoxPayload = {
        project_id: Number(projectId),
        name: name.trim() || undefined,
        address: address.trim(),
        default_password: defaultPassword,
        type: ALWAYS_ONLINE,
      };
      return activateStock(stock.id, numericCompanyId, box);
    },
    {
      onSuccess: () => {
        toast.success(`Caixa CMB ${stock.cmb_number} ativada com sucesso.`);
        qc.invalidateQueries(['admin-manufacturer-stock']);
        onClose();
      },
      onError: (e: any) => { toast.error(`Erro: ${e?.response?.data?.message ?? e.message}`); },
    },
  );

  const canSubmit =
    numericCompanyId > 0 &&
    !!projectId &&
    name.trim().length > 0 &&
    address.trim().length > 0 &&
    defaultPassword.length > 0 &&
    !activate.isLoading;

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 560 }}>
        <PageCard>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <SectionLabel>Ativar caixa CMB {stock.cmb_number}</SectionLabel>
            <button type='button' onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>
              <X size={18} color={TOKENS.color.muted} />
            </button>
          </div>
          <div style={{ fontSize: 12, color: TOKENS.color.muted, lineHeight: 1.5, marginBottom: 14 }}>
            Bluetooth <code style={codeStyle}>{stock.bluetooth}</code> · Fabricante: {stock.manufacturer_name ?? `#${stock.manufacturer_id}`}.
            A caixa será criada como <strong>online</strong> na empresa selecionada e ficará pronta pra uso imediato.
            Sensibilidades e demais ajustes podem ser feitos depois pela tela de Caixas.
          </div>

          <Field label='Empresa *'>
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} style={inputStyle}>
              <option value=''>Selecione…</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </Field>

          <Field label='Projeto *'>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              disabled={!numericCompanyId || projectsQuery.isLoading}
              style={inputStyle}
            >
              <option value=''>
                {!numericCompanyId
                  ? 'Selecione uma empresa primeiro'
                  : projectsQuery.isLoading
                    ? 'Carregando…'
                    : (projectsQuery.data?.length === 0 ? 'Empresa sem projetos — cadastre um' : 'Selecione…')}
              </option>
              {(projectsQuery.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </Field>

          <Field label='Nome *'>
            <input type='text' value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
          </Field>

          <Field label='Endereço *'>
            <input type='text' value={address} onChange={(e) => setAddress(e.target.value)} style={inputStyle} placeholder='Rua, número, bairro, cidade' />
          </Field>

          <Field label='Senha padrão *'>
            <input type='text' value={defaultPassword} onChange={(e) => setDefaultPassword(e.target.value)} style={inputStyle} placeholder='Senha de fábrica' />
          </Field>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
            <button type='button' onClick={onClose} style={ghostBtn}>Cancelar</button>
            <button
              type='button'
              disabled={!canSubmit}
              onClick={() => activate.mutate()}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: TOKENS.color.action, color: '#fff', border: 'none',
                padding: '8px 16px', fontSize: 13, fontWeight: 700,
                borderRadius: TOKENS.radius.chip,
                opacity: canSubmit ? 1 : 0.5,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
              }}
            >
              <CheckCircle2 size={14} /> {activate.isLoading ? 'Ativando…' : 'Ativar caixa'}
            </button>
          </div>
        </PageCard>
      </div>
    </div>
  );
};

const Field: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: TOKENS.color.muted }}>
      {label}
    </span>
    {children}
  </label>
);

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', fontSize: 13,
  border: `1px solid ${TOKENS.color.border}`, borderRadius: TOKENS.radius.chip,
  background: TOKENS.color.card, color: TOKENS.color.text, outline: 'none',
};
const ghostBtn: React.CSSProperties = {
  background: TOKENS.color.card, color: TOKENS.color.text,
  border: `1px solid ${TOKENS.color.border}`,
  padding: '8px 14px', fontSize: 13, fontWeight: 600,
  borderRadius: TOKENS.radius.chip, cursor: 'pointer',
};
const modalOverlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.55)',
  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  padding: '60px 20px', zIndex: 1000, overflowY: 'auto',
};
const codeStyle: React.CSSProperties = {
  fontFamily: 'monospace', fontSize: 11, padding: '1px 5px',
  background: TOKENS.color.surface, borderRadius: 3,
};
